import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  formatMoney,
  type LedgerCycle,
  type ReallocationPart,
  type ReallocationPreview,
  type ReallocationRequest,
  reallocationSource,
  toCents,
} from '@k-management/shared';
import { useEffect, useRef, useState } from 'react';
import { apiRequest } from '../../lib/api.js';
import { formatDate } from '../../lib/format.js';
import { announceTreasuryChange } from './treasury-ui.js';

const parts = (list: readonly ReallocationPart[], bankName: (code: string) => string) =>
  list.length
    ? list
        .map(
          (part) =>
            `${reallocationSource(part, bankName)} ${formatDate(part.date)} ${formatMoney(part.amount)}`,
        )
        .join(' + ')
    : '—';

/**
 * «Riabbina in ordine di data» (2.23): the preview of the new links of the
 * subject's invoices with his payments, the oldest to the oldest, and the
 * confirmation that applies them all at once.
 */
export function ReallocationDialog({
  subjectId,
  cycle,
  bankName,
  onClose,
}: {
  subjectId: string;
  cycle: LedgerCycle;
  bankName: (code: string) => string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const dialogRef = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);
  const [all, setAll] = useState(false);
  const url = `/treasury/statements/${subjectId}/reallocation`;
  const preview = useQuery({
    queryKey: ['treasury', 'reallocation', subjectId, cycle],
    queryFn: () => apiRequest<ReallocationPreview>(`${url}?cycle=${cycle}`),
  });
  const apply = useMutation({
    mutationFn: () =>
      apiRequest<ReallocationPreview>(url, {
        method: 'POST',
        body: { cycle } satisfies ReallocationRequest,
      }),
    onSuccess: async () => {
      announceTreasuryChange();
      await queryClient.invalidateQueries({ queryKey: ['treasury'] });
      onClose();
    },
  });
  const data = preview.data;
  const shown = data ? data.invoices.filter((item) => all || item.changed) : [];
  return (
    <dialog ref={dialogRef} className="dialog wide-dialog" onClose={onClose}>
      <div className="stack">
        <h2>Riabbina in ordine di data{data ? ` · ${data.subjectName}` : ''}</h2>
        <p className="muted small">
          Le fatture più vecchie prendono i pagamenti più vecchi. Si spostano solo i pagamenti di
          banca già collegati alle fatture del soggetto e quelli senza documento; note di credito,
          compensazioni, chiusure, abbuoni, incassi trattenuti dalla banca e le parti di altri
          soggetti restano come sono. Finché non confermi non cambia nulla.
        </p>
        {preview.isPending && <p className="muted">Calcolo…</p>}
        {preview.error && <p className="error">{preview.error.message}</p>}
        {data && (
          <>
            <div className="treasury-tiles">
              <div className="treasury-tile">
                <span>Fatture</span>
                <strong>
                  {data.invoices.length} · {formatMoney(data.totals.invoices)}
                </strong>
              </div>
              <div className="treasury-tile">
                <span>Pagamenti considerati</span>
                <strong>{formatMoney(data.totals.payments)}</strong>
              </div>
              <div className="treasury-tile">
                <span>Fatture il cui abbinamento cambia</span>
                <strong>{data.changed}</strong>
              </div>
              <div className="treasury-tile">
                <span>Resta senza documento</span>
                <strong>{formatMoney(data.totals.withoutDocument)}</strong>
              </div>
              {toCents(data.totals.open) > 0 && (
                <div className="treasury-tile bad">
                  <span>Resta da pagare sulle fatture</span>
                  <strong>{formatMoney(data.totals.open)}</strong>
                </div>
              )}
            </div>
            <label className="checkbox small">
              <input
                type="checkbox"
                checked={all}
                onChange={(event) => setAll(event.target.checked)}
              />{' '}
              mostra anche le fatture che non cambiano
            </label>
            <div className="table-wrap" style={{ maxHeight: '40vh' }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Fattura</th>
                    <th className="numeric">Da coprire</th>
                    <th>Oggi</th>
                    <th>Dopo</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((item) => (
                    <tr key={item.id}>
                      <td>
                        {item.number}
                        <small className="muted block">del {formatDate(item.date)}</small>
                      </td>
                      <td className="numeric">{formatMoney(item.toCover)}</td>
                      <td className="small muted">{parts(item.current, bankName)}</td>
                      <td className="small">
                        {item.changed ? parts(item.next, bankName) : 'uguale'}
                        {toCents(item.open) > 0 && (
                          <span className="warn block">resta {formatMoney(item.open)}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {shown.length === 0 && (
                    <tr>
                      <td colSpan={4} className="muted">
                        Nessuna fattura cambia: i pagamenti sono già in ordine di data.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {data.leftovers.length > 0 && (
              <>
                <h3>Senza documento dopo il riabbinamento</h3>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Data</th>
                      <th>Banca</th>
                      <th className="numeric">Del soggetto</th>
                      <th className="numeric">Senza documento</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.leftovers.map((item) => (
                      <tr key={item.movementId}>
                        <td>{formatDate(item.date)}</td>
                        <td>{reallocationSource(item, bankName)}</td>
                        <td className="numeric">{formatMoney(item.amount)}</td>
                        <td className="numeric">
                          <strong>{formatMoney(item.withoutDocument)}</strong>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
            <p className="muted small">
              Tutto in un solo passaggio; nel registro attività resta l’abbinamento di prima di ogni
              fattura. Rifatto sugli stessi dati dà lo stesso risultato.
            </p>
          </>
        )}
        {apply.error && <p className="error">{apply.error.message}</p>}
        <div className="actions">
          <span className="grid-spacer" />
          <button type="button" onClick={onClose}>
            Annulla
          </button>
          <button
            type="button"
            className="primary"
            disabled={
              !data || apply.isPending || (data.changed === 0 && data.leftovers.length === 0)
            }
            onClick={() => apply.mutate()}
          >
            {apply.isPending
              ? 'Attendere…'
              : data
                ? `Riabbina ${data.changed} ${data.changed === 1 ? 'fattura' : 'fatture'}`
                : 'Riabbina'}
          </button>
        </div>
      </div>
    </dialog>
  );
}
